import {
  SortBy,
  MapPinRequest,
  MapPinResponse,
  MapPageFilter,
} from '@zoocasa/go-search';
import { fetchWithRetry } from 'utils/fetchWithRetry';
import { FetchOptions } from 'types/fetchWithRetry';
import { HttpRequestMethodTypes, HttpStatusCode } from 'types';
import { getGoSearchHost, isServerSide } from 'utils/host-config';

/**
 * Server-side cap on `offset + limit` for /api/map/pins. Matches go-search's
 * `MaxPinOffset`. Going past this returns HTTP 400 "Offset out of bounds".
 */
export const MAX_PIN_OFFSET = 5000;

export async function fetchPins(
  filter: MapPageFilter,
  sortBy: SortBy = SortBy.DateDesc,
  signal?: AbortSignal,
  limit: number = 200,
  offset: number = 0,
  host: string = getGoSearchHost(isServerSide()),
  includeListings: boolean = false
): Promise<MapPinResponse> {
  const safeOffset = Number.isFinite(offset) && offset > 0 ? Math.floor(offset) : 0;
  const offsetParam = safeOffset > 0 ? `&offset=${safeOffset}` : '';
  const pinsEndpointUrl = `${host}/api/map/pins?limit=${limit}${offsetParam}`;
  let resp: MapPinResponse;

  try {
    const mapPinRequest = MapPinRequest.fromPartial({ sortBy, filter, includeListings });
    const requestBody = MapPinRequest.encode(mapPinRequest).finish();

    const options: FetchOptions = {
      method: HttpRequestMethodTypes.POST,
      body: new Uint8Array(requestBody),
      signal,
    };
    const response = await fetchWithRetry(pinsEndpointUrl, options);

    if (response.ok) {
      const content = await response.blob();
      const buffer = await content.arrayBuffer();
      const bytes = new Uint8Array(buffer);
      // Always protobuf-decode; do not treat Content-Length / small bodies as failure — go-search may send
      // a minimal MapPinResponse (e.g. responseFormatVersion only) with a tiny body.
      resp = MapPinResponse.decode(bytes);
    } else {
      // 400 from /api/map/pins means offset+limit > MAX_PIN_OFFSET. Terminal — no retry path —
      // surface as an empty response so callers can fall back / show empty state.
      if (response.status === HttpStatusCode.BAD_REQUEST) {
        console.error(`fetch pins offset out of bounds (limit=${limit}, offset=${safeOffset}): ${response.statusText}`);
      } else {
        console.error(`fetch pins within boundary: ${response.status} ${response.statusText} ${response.type}`);
      }
      resp = MapPinResponse.fromPartial({});
    }
  } catch (error: unknown) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw error;
    }
    console.error('failed to fetch map pins: %s', error);
    resp = MapPinResponse.fromPartial({});
  }

  return resp;
}
